/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Headers,
  RequiredKeys,
  ColumnVisibility,
  ColumnOrdering,
  ColumnPinning,
  ColumnFiltering,
  RowSorting,
  ColumnGrouping,
  RowExpanding,
  RowSelection,
  ColumnSizing,
  memo,
  createColumn,
  functionalUpdate,
  RowPagination,
} from '@tanstack/react-table';
import {
  ColumnFaceting,
  GlobalFaceting,
  GlobalFiltering,
} from '@tanstack/table-core';

import type {
  Column,
  ColumnDef,
  ColumnDefResolved,
  GroupColumnDef,
  InitialTableState,
  Row,
  RowData,
  RowModel,
  Table,
  TableFeature,
  TableMeta,
  TableOptions,
  TableOptionsResolved,
  TableState,
  Updater,
} from './types.js';
import {
  isDev,
  isProduction,
} from '../../../../core/utils/is-node-environment.js';
import { DataTableV2ColumnFontStyle } from '../../features/ColumnFontStyle.js';
import { DataTableV2ColumnOrder } from '../../features/ColumnOrder/ColumnOrder.js';
import { DataTableV2ColumnSizing } from '../../features/ColumnSizing.js';
import { DataTableV2ColumnVisibility } from '../../features/ColumnVisibility/ColumnVisibility.js';
import { DataTableV2DownloadData } from '../../features/Download/Download.js';
import { Layout } from '../../features/Layout.js';
import { LineWrap } from '../../features/LineWrap.js';
import { DataTableV2PaginationFeature } from '../../features/Pagination/Pagination.js';
import { DataTableV2RowDetails } from '../../features/RowDetails/RowDetails.js';
import { DataTableV2RowInteractivity } from '../../features/RowInteractivity/RowInteractivity.js';
import { DataTableV2RowOrder } from '../../features/RowOrder/RowOrder.js';
import { DataTableV2RowSelection } from '../../features/RowSelection/RowSelection.js';
import { Sorting as DataTableV2Sorting } from '../../features/Sorting/Sorting.js';
import { DataTableV2SubRows } from '../../features/SubRows/SubRows.js';
import { Thresholds } from '../../features/Thresholds/Thresholds.js';
import { UserActions } from '../../features/UserActions/UserActions.js';
import { DataTableV2VirtualScrolling } from '../../features/VirtualScrolling/VirtualScrolling.js';
import { VisualVariantConfig } from '../../features/VisualVariants.js';

const features = [
  Headers,
  ColumnVisibility,
  ColumnOrdering,
  ColumnPinning,
  ColumnFaceting,
  ColumnFiltering,
  GlobalFaceting, //depends on ColumnFaceting
  GlobalFiltering, //depends on ColumnFiltering
  RowSorting,
  ColumnGrouping,
  RowExpanding,
  RowPagination,
  RowSelection,
  ColumnSizing,
  DataTableV2ColumnSizing,
  LineWrap,
  Layout,
  VisualVariantConfig,
  UserActions,
  DataTableV2Sorting,
  DataTableV2ColumnVisibility,
  DataTableV2ColumnOrder,
  DataTableV2RowSelection,
  Thresholds,
  DataTableV2SubRows,
  DataTableV2RowDetails,
  DataTableV2RowInteractivity,
  DataTableV2VirtualScrolling,
  DataTableV2DownloadData,
  DataTableV2ColumnFontStyle,
  DataTableV2PaginationFeature,
  DataTableV2RowOrder,
] as const;

/** @internal */
export interface CoreOptions<TData extends RowData> {
  /**
   * Set this option to override any of the `autoReset...` feature options.
   */
  autoResetAll?: boolean;
  /**
   * The array of column defs to use for the table.
   */
  columns: ColumnDef<TData, any>[];
  /**
   * The data for the table to display. This array should match the type you provided to `table.setRowType<...>`. Columns can access this data via string/index or a functional accessor. When the `data` option changes reference, the table will reprocess the data.
   */
  data: TData[];
  /**
   * Set this option to `true` to output all debugging information to the console.
   */
  debugAll?: boolean;
  /**
   * Set this option to `true` to output cell debugging information to the console.
   */
  debugCells?: boolean;
  /**
   * Set this option to `true` to output column debugging information to the console.
   */
  debugColumns?: boolean;
  /**
   * Set this option to `true` to output header debugging information to the console.
   */
  debugHeaders?: boolean;
  /**
   * Set this option to `true` to output row debugging information to the console.
   */
  debugRows?: boolean;
  /**
   * Set this option to `true` to output table debugging information to the console.
   */
  debugTable?: boolean;
  /**
   * Default column options to use for all column defs supplied to the table.
   */
  defaultColumn?: Partial<ColumnDef<TData, unknown>>;
  /**
   * This required option is a factory for a function that computes and returns the core row model for the table.
   */
  getCoreRowModel: (table: Table<any>) => () => RowModel<any>;
  /**
   * This optional function is used to derive a unique ID for any given row. If not provided the rows index is used (nested rows join together with `.` using their grandparents' index eg. `index.index.index`). If you need to identify individual rows that are originating from any server-side operations, it's suggested you use this function to return an ID that makes sense regardless of network IO/ambiguity eg. a userId, taskId, database ID field, etc.
   */
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  /**
   * This optional function is used to access the sub rows for any given row. If you are using nested rows, you will need to use this function to return the sub rows object (or undefined) from the row.
   */
  getSubRows?: (originalRow: TData, index: number) => undefined | TData[];
  /**
   * Use this option to optionally pass initial state to the table. This state will be used when resetting various table states either automatically by the table (eg. `options.autoResetPageIndex`) or via functions like `table.resetRowSelection()`. Most reset function allow you optionally pass a flag to reset to a blank/default state instead of the initial state.
   *
   * Table state will not be reset when this object changes, which also means that the initial state object does not need to be stable.
   *
   */
  initialState?: InitialTableState;
  /**
   * This option is used to optionally implement the merging of table options.
   */
  mergeOptions?: (
    defaultOptions: TableOptions<TData>,
    options: Partial<TableOptions<TData>>,
  ) => TableOptions<TData>;
  /**
   * You can pass any object to `options.meta` and access it anywhere the `table` is available via `table.options.meta`.
   */
  meta?: TableMeta<TData>;
  /**
   * The `onStateChange` option can be used to optionally listen to state changes within the table.
   */
  onStateChange: (updater: Updater<TableState>) => void;
  /**
   * Value used when the desired value is not found in the data.
   */
  renderFallbackValue: any;
  /**
   * The `state` option can be used to optionally _control_ part or all of the table state. The state you pass here will merge with and overwrite the internal automatically-managed state to produce the final state for the table. You can also listen to state changes via the `onStateChange` option.
   * Note: Any state passed in here will override both the internal state and any other `initialState` you provide.
   */
  state: Partial<TableState>;
}

/** @internal */
export interface CoreInstance<TData extends RowData> {
  _features: readonly TableFeature[];
  _getAllFlatColumnsById: () => Record<string, Column<TData, unknown>>;
  _getColumnDefs: () => ColumnDef<TData, unknown>[];
  _getCoreRowModel?: () => RowModel<TData>;
  _getDefaultColumnDef: () => Partial<ColumnDef<TData, unknown>>;
  _getRowId: (_: TData, index: number, parent?: Row<TData>) => string;
  _queue: (cb: () => void) => void;
  /**
   * Returns all columns in the table in their normalized and nested hierarchy.
   */
  getAllColumns: () => Column<TData, unknown>[];
  /**
   * Returns all columns in the table flattened to a single level.
   */
  getAllFlatColumns: () => Column<TData, unknown>[];
  /**
   * Returns all columns in the table flattened to a single level, excluding builtin columns.
   */
  getAllFlatColumnsWithoutBuiltin: () => Column<TData, unknown>[];
  /**
   * Returns all column IDs in the table flattened to a single level, excluding builtin columns.
   */
  getAllFlatColumnIdsWithoutBuiltin: () => string[];
  /**
   * Returns the total number of enabled builtin columns in the table.
   */
  getBuiltinColumnsCount: () => number;
  /**
   * Returns all leaf-node columns in the table flattened to a single level. This does not include parent columns.
   */
  getAllLeafColumns: () => Column<TData, unknown>[];
  /**
   * Returns a single column by its ID.
   */
  getColumn: (columnId: string) => Column<TData, unknown> | undefined;
  /**
   * Returns the core row model before any processing has been applied.
   */
  getCoreRowModel: () => RowModel<TData>;
  /**
   * Returns the row with the given ID.
   */
  getRow: (id: string, searchAll?: boolean) => Row<TData>;
  /**
   * Returns the final model after all processing from other used features has been applied. This is the row model that is most commonly used for rendering.
   */
  getRowModel: () => RowModel<TData>;
  /**
   * Call this function to get the table's current state. It's recommended to use this function and its state, especially when managing the table state manually. It is the exact same state used internally by the table for every feature and function it provides.
   */
  getState: () => TableState;
  /**
   * This is the resolved initial state of the table.
   */
  initialState: TableState;
  /**
   * A read-only reference to the table's current options.
   */
  options: RequiredKeys<TableOptionsResolved<TData>, 'state'>;
  /**
   * Call this function to reset the table state to the initial state.
   */
  reset: () => void;
  /**
   * This function can be used to update the table options.
   */
  setOptions: (newOptions: Updater<TableOptionsResolved<TData>>) => void;
  /**
   * Call this function to update the table state.
   */
  setState: (updater: Updater<TableState>) => void;
}

/** @internal */
// eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type
export interface CoreTableState {}

/** @internal */
export function createTable<TData extends RowData>(
  options: TableOptionsResolved<TData>,
): Table<TData> {
  if (options.debugAll || options.debugTable) {
    // eslint-disable-next-line no-console
    console.info('Creating Table Instance...');
  }

  const table = { _features: features } as unknown as Table<TData>;

  const defaultOptions = table._features.reduce((obj, feature) => {
    return Object.assign(obj, feature.getDefaultOptions?.(table));
  }, {}) as TableOptionsResolved<TData>;

  const mergeOptions = (options: TableOptionsResolved<TData>) => {
    if (table.options.mergeOptions) {
      return table.options.mergeOptions(defaultOptions, options);
    }

    return {
      ...defaultOptions,
      ...options,
    };
  };

  const coreInitialState: CoreTableState = {};

  let initialState = {
    ...coreInitialState,
    ...(options.initialState ?? {}),
  } as TableState;

  table._features.forEach((feature) => {
    initialState = feature.getInitialState?.(initialState) ?? initialState;
  });

  const queued: (() => void)[] = [];
  let queuedTimeout = false;

  const coreInstance: CoreInstance<TData> = {
    _features: features,
    options: {
      ...defaultOptions,
      ...options,
    },
    initialState,
    _queue: (cb) => {
      queued.push(cb);

      if (!queuedTimeout) {
        queuedTimeout = true;

        // Schedule a microtask to run the queued callbacks after
        // the current call stack (render, etc) has finished.
        Promise.resolve()
          .then(() => {
            while (queued.length) {
              queued.shift()?.();
            }
            queuedTimeout = false;
          })
          .catch((error) =>
            setTimeout(() => {
              throw error;
            }),
          );
      }
    },
    reset: () => {
      table.setState(table.initialState);
    },
    setOptions: (updater) => {
      const newOptions = functionalUpdate(updater, table.options);
      table.options = mergeOptions(newOptions) as RequiredKeys<
        TableOptionsResolved<TData>,
        'state'
      >;
    },

    getState: () => {
      return table.options.state as TableState;
    },

    setState: (updater: Updater<TableState>) => {
      table.options.onStateChange?.(updater);
    },

    _getRowId: (row: TData, index: number, parent?: Row<TData>) =>
      table.options.getRowId?.(row, index, parent) ??
      `${parent ? [parent.id, index].join('.') : index}`,

    getCoreRowModel: () => {
      if (!table._getCoreRowModel) {
        table._getCoreRowModel = table.options.getCoreRowModel(table);
      }

      return table._getCoreRowModel();
    },

    // The final calls start at the bottom of the model,
    // row ordering, which then work their way up
    getRowModel: () => {
      return table.getRowDetailsRowModel();
    },
    getRow: (id: string, searchAll?: boolean) => {
      const row = (searchAll ? table.getCoreRowModel() : table.getRowModel())
        .rowsById[id];

      if (!row) {
        if (!isProduction()) {
          throw new Error(`getRow expected an ID, but got ${id}`);
        }
        throw new Error();
      }

      return row;
    },
    _getDefaultColumnDef: memo(
      () => [table.options.defaultColumn],
      (defaultColumn) => {
        defaultColumn = (defaultColumn ?? {}) as Partial<
          ColumnDef<TData, unknown>
        >;

        return {
          header: (props) => {
            const resolvedColumnDef = props.header.column
              .columnDef as ColumnDefResolved<TData>;

            if (resolvedColumnDef.accessorKey) {
              return resolvedColumnDef.accessorKey;
            }

            if (resolvedColumnDef.accessorFn) {
              return resolvedColumnDef.id;
            }

            return null;
          },
          // footer: props => props.header.column.id,
          cell: (props) => props.renderValue<any>()?.toString?.() ?? null,
          ...table._features.reduce((obj, feature) => {
            return Object.assign(obj, feature.getDefaultColumnDef?.());
          }, {}),
          ...defaultColumn,
        } as Partial<ColumnDef<TData, unknown>>;
      },
      {
        debug: () => table.options.debugAll ?? table.options.debugColumns,
        key: isDev() && 'getDefaultColumnDef',
      },
    ),

    _getColumnDefs: () => table.options.columns,

    getAllColumns: memo(
      () => [table._getColumnDefs()],
      (columnDefs) => {
        const recurseColumns = (
          columnDefs: ColumnDef<TData, unknown>[],
          parent?: Column<TData, unknown>,
          depth = 0,
        ): Column<TData, unknown>[] => {
          return columnDefs.map((columnDef) => {
            const column = createColumn(
              // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
              // @ts-ignore Strict mode transition - needs to be investigated
              table,
              columnDef,
              depth,
              parent,
            ) as unknown as Column<TData, unknown>;

            const groupingColumnDef = columnDef as GroupColumnDef<
              TData,
              unknown
            >;

            column.columns = groupingColumnDef.columns
              ? recurseColumns(groupingColumnDef.columns, column, depth + 1)
              : [];

            return column;
          });
        };

        return recurseColumns(columnDefs);
      },
      {
        key: isDev() && 'getAllColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllFlatColumns: memo(
      () => [table.getAllColumns()],
      (allColumns) => {
        return allColumns.flatMap((column) => {
          return column.getFlatColumns() as Column<TData, unknown>[];
        });
      },
      {
        key: isDev() && 'getAllFlatColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllFlatColumnsWithoutBuiltin: memo(
      () => [table.getAllFlatColumns()],
      (allFlatColumns) => {
        return allFlatColumns.filter(
          (leafColumn) => !leafColumn.columnDef.meta?.builtin,
        );
      },
      {
        key: isDev() && 'getAllFlatColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllFlatColumnIdsWithoutBuiltin: memo(
      () => [table.getAllFlatColumnsWithoutBuiltin()],
      (allFlatColumnsWithoutBuiltIn) => {
        return allFlatColumnsWithoutBuiltIn.map((column) => column.id);
      },
      {
        key: isDev() && 'getAllFlatColumnIdsWithoutBuiltin',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getBuiltinColumnsCount: memo(
      () => {
        const isRowOrderDragAndDropEnabled =
          table.options.enableRowOrderDragAndDrop !== undefined &&
          table.options.enableRowOrderDragAndDrop !== false;
        const isRowSelectionEnabled =
          table.options.enableRowSelection !== undefined &&
          table.options.enableRowSelection !== false;
        const isRowDetailsEnabled = table.options.enableRowDetails === true;

        return [
          isRowOrderDragAndDropEnabled,
          isRowSelectionEnabled,
          isRowDetailsEnabled,
        ];
      },
      (
        isRowOrderDragAndDropEnabled,
        isRowSelectionEnabled,
        isRowDetailsEnabled,
      ) => {
        let builtinColumnsCount = 0;
        if (isRowOrderDragAndDropEnabled) {
          builtinColumnsCount++;
        }
        if (isRowSelectionEnabled) {
          builtinColumnsCount++;
        }
        if (isRowDetailsEnabled) {
          builtinColumnsCount++;
        }
        return builtinColumnsCount;
      },
      {
        key: isDev() && 'getBuiltinColumnsCount',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    _getAllFlatColumnsById: memo(
      () => [table.getAllFlatColumns()],
      (flatColumns) => {
        return (flatColumns as Column<TData, unknown>[]).reduce(
          (acc, column) => {
            acc[column.id] = column;
            return acc;
          },
          {} as Record<string, Column<TData, unknown>>,
        );
      },
      {
        key: isDev() && 'getAllFlatColumnsById',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllLeafColumns: memo(
      () => [table.getAllColumns(), table._getOrderColumnsFn()],
      (allColumns, orderColumns) => {
        const leafColumns = allColumns.flatMap((column) =>
          column.getLeafColumns(),
        );
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
        // @ts-ignore Strict mode transition - needs to be investigated
        return orderColumns(leafColumns) as unknown as Column<TData, unknown>[];
      },
      {
        key: isDev() && 'getAllLeafColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getColumn: (columnId) => {
      const column = table._getAllFlatColumnsById()[columnId] as Column<
        TData,
        unknown
      >;

      if (!isProduction() && !column) {
        // eslint-disable-next-line no-console
        console.log(`[Table] Column with id '${columnId}' does not exist.`);
      }

      return column;
    },
  };

  Object.assign(table, coreInstance);

  for (let index = 0; index < table._features.length; index++) {
    const feature = table._features[index];
    feature?.createTable?.(table);
  }

  return table;
}
